import type { TagProps } from "@octopusdeploy/design-system-components";
import { Tag as DesignSystemTag } from "@octopusdeploy/design-system-components";
import { ParentEnvironmentIcon, EphemeralEnvironmentIcon, TenantsIcon, EnvironmentIcon } from "@octopusdeploy/design-system-icons";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import type { EnvironmentV2Resource, TenantResource, ProjectResource, IId } from "@octopusdeploy/octopus-server-client";
import type { ReactElement } from "react";
import { client } from "~/clientInstance";
import Tag from "./Tag";
type MissingResource = {
    Id: string;
    Name: string;
};
function matchesToTags<T extends IId>(set: T[], selected: string[], createTag: (x: T) => TagProps): TagProps[] {
    return set.filter((item) => selected.includes(item.Id)).map((item) => createTag(item));
}
function matchesToTagsOrMissing<T extends IId>(set: T[], selected: string[], createTag: (x: T, isMissing: boolean) => TagProps): TagProps[] {
    const dict: Record<string, T> = set.reduce((acc: Record<string, T>, item) => {
        acc[item.Id] = item;
        return acc;
    }, {});
    return selected.map((id) => {
        const val = dict[id] ?? { Id: id, Name: "unknown" };
        return createTag(val, dict[id] === undefined);
    });
}
function createProjectTag(project: ProjectResource): TagProps {
    return {
        label: project.Name,
        prefix: <Avatar type="project" size="small" src={client.resolve(project.Links.Logo)} alt={`${project.Name} Logo`}/>,
    };
}
function createMissingProjectTag(project: MissingResource): TagProps {
    const label = project.Name === "unknown" ? "Missing Resource" : project.Name;
    return {
        label,
        prefix: <Avatar type="project" size="small" src="" alt={`${label} Logo`}/>,
    };
}
function createEnvironmentV2Tag(environment: EnvironmentV2Resource): TagProps {
    const getEnvironmentIcon = (): ReactElement => {
        switch (environment.Type) {
            case "Ephemeral":
                return <EphemeralEnvironmentIcon size={16}/>;
            case "Parent":
                return <ParentEnvironmentIcon size={16}/>;
            default:
                return <EnvironmentIcon size={16}/>;
        }
    };
    return {
        label: environment.Name,
        prefix: getEnvironmentIcon(),
    };
}
function createMissingEnvironmentTag(environment: MissingResource): TagProps {
    return {
        label: environment.Name === "unknown" ? "Missing Resource" : environment.Name,
    };
}
function createTenantTag(tenant: TenantResource): TagProps {
    return {
        label: tenant.Name,
        prefix: <TenantsIcon size={16}/>,
    };
}
function createMissingTenantTag(tenant: MissingResource): TagProps {
    return {
        label: tenant.Name === "unknown" ? "Missing Resource" : tenant.Name,
    };
}
export function projectTagList(set: ProjectResource[], selectedIds: string[]): TagProps[] {
    return matchesToTags(set, selectedIds, createProjectTag);
}
export function projectTagWithMissingList(set: ProjectResource[], selectedIds: string[]): TagProps[] {
    return matchesToTagsOrMissing(set, selectedIds, (x, isMissing) => {
        if (isMissing) {
            return createMissingProjectTag(x);
        }
        return createProjectTag(x);
    });
}
export function environmentV2TagList(set: EnvironmentV2Resource[], selectedIds: string[]): TagProps[] {
    return matchesToTags(set, selectedIds, createEnvironmentV2Tag);
}
export function environmentV2WithMissingTagList(set: EnvironmentV2Resource[], selectedIds: string[]): TagProps[] {
    return matchesToTagsOrMissing(set, selectedIds, (x, isMissing) => (isMissing ? createMissingEnvironmentTag(x) : createEnvironmentV2Tag(x)));
}
export function tenantTagList(set: TenantResource[], selectedIds: string[]): TagProps[] {
    return matchesToTags(set, selectedIds, createTenantTag);
}
function createSimpleTenantTag(tenant: {
    Id: string;
    Name: string;
}): TagProps {
    return {
        label: tenant.Name,
        prefix: <TenantsIcon size={16}/>,
    };
}
export function tenantTagWithMissingList(set: {
    Id: string;
    Name: string;
}[], selectedIds: string[]): TagProps[] {
    return matchesToTagsOrMissing(set, selectedIds, (x, isMissing) => (isMissing ? createMissingTenantTag(x) : createSimpleTenantTag(x)));
}
export const AllEnvironmentsTag = () => {
    return <DesignSystemTag label="All environments" prefix={<EnvironmentIcon size={16}/>}/>;
};
export default Tag;
